import React, { useState } from "react";
import { X, ArrowRight, MessageCircle, CheckCircle2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { newEventId } from "../lib/track";

const SERVICES = ["Music Production", "Music Video & Visuals", "Artist Branding", "Music Marketing", "Artist Launch", "Course / Academy", "General"];
const BUDGETS = ["Under ₹10k", "₹10k–₹25k", "₹25k–₹50k", "₹50k–₹1L", "₹1L+"];

export default function LeadModal({ open, service, onClose, trackEvent, openWhatsApp }) {
    const [form, setForm] = useState({ name: "", email: "", whatsapp: "", instagram: "", company: "", budget: "", description: "", timeline: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    if (!open) return null;
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const eid = newEventId();
            await api.post("/leads", { ...form, service, source: window.location.pathname, event_id: eid });
            trackEvent?.("Lead", { service }, { eventID: eid });
            setDone(true);
        } catch (err) { alert(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const close = () => { setDone(false); onClose(); };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="lead-modal">
            <div className="absolute inset-0 bg-espresso/60 backdrop-blur-sm" onClick={close} />
            <div className="relative w-full max-w-lg bg-white shadow-2xl max-h-[92vh] overflow-y-auto no-scrollbar">
                <button onClick={close} className="absolute top-4 right-4 text-neutral-400 hover:text-ink" data-testid="lead-close"><X className="h-5 w-5" /></button>
                {done ? (
                    <div className="p-10 text-center">
                        <CheckCircle2 className="h-12 w-12 text-brand-gold mx-auto" />
                        <h3 className="mt-4 font-display font-semibold text-2xl">Request received</h3>
                        <p className="mt-2 text-neutral-500">Thanks! The MUVIARTS team will reach out shortly. For a faster reply, message us on WhatsApp.</p>
                        <button onClick={() => openWhatsApp?.("launch")} className="btn-primary mt-6 w-full" data-testid="lead-whatsapp"><MessageCircle className="h-4 w-4" /> Chat on WhatsApp</button>
                        <button onClick={close} className="btn-outline mt-3 w-full">Close</button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="p-7 sm:p-9" data-testid="lead-form">
                        <p className="overline mb-2">Start Your Project</p>
                        <h3 className="font-display font-semibold text-2xl">Tell us about {service === "General" ? "your project" : service.toLowerCase()}</h3>
                        <p className="mt-1 text-sm text-neutral-500">We'll get back within one business day.</p>
                        <div className="mt-5 grid sm:grid-cols-2 gap-3">
                            <input required placeholder="Name" value={form.name} onChange={set("name")} data-testid="lead-name" className="border border-[#d9cfbc] px-4 py-3 outline-none focus:border-ink" />
                            <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="lead-email" className="border border-[#d9cfbc] px-4 py-3 outline-none focus:border-ink" />
                            <input placeholder="WhatsApp number" value={form.whatsapp} onChange={set("whatsapp")} data-testid="lead-whatsapp-num" className="border border-[#d9cfbc] px-4 py-3 outline-none focus:border-ink" />
                            <input placeholder="Instagram handle" value={form.instagram} onChange={set("instagram")} className="border border-[#d9cfbc] px-4 py-3 outline-none focus:border-ink" />
                            <input placeholder="Artist / Company" value={form.company} onChange={set("company")} className="border border-[#d9cfbc] px-4 py-3 outline-none focus:border-ink" />
                            <select value={form.budget} onChange={set("budget")} data-testid="lead-budget" className="border border-[#d9cfbc] px-4 py-3 outline-none focus:border-ink bg-white text-neutral-700">
                                <option value="">Budget range</option>
                                {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </div>
                        <textarea rows={3} placeholder="Project description" value={form.description} onChange={set("description")} className="mt-3 w-full border border-[#d9cfbc] px-4 py-3 outline-none focus:border-ink resize-none" />
                        <input placeholder="Desired timeline (e.g. 3 weeks)" value={form.timeline} onChange={set("timeline")} className="mt-3 w-full border border-[#d9cfbc] px-4 py-3 outline-none focus:border-ink" />
                        <button disabled={busy} className="btn-primary w-full mt-5 disabled:opacity-60" data-testid="lead-submit">{busy ? "Sending…" : "Send Request"} <ArrowRight className="h-4 w-4" /></button>
                    </form>
                )}
            </div>
        </div>
    );
}
